import * as React from 'react';
import { Github } from "@app/Dashboard/git"
import { Link } from "react-router-dom";

import {
  Table,
  Thead,
  Tr,
  Th,
  Td,
  Tbody,
  ThProps,
} from '@patternfly/react-table';

import {
  Popover,
  Tooltip,
  List,
  ListItem,
} from '@patternfly/react-core'

import Timestamp from 'react-timestamp';

import { RunningIcon, AsleepIcon, InProgressIcon, OutlinedQuestionCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, WrenchIcon, NetworkIcon } from '@patternfly/react-icons'

import { Namespace, Resource } from '@app/Dashboard/models';


interface NamespaceResourcesTableProps {
  namespace: Namespace,
}


const NamespaceResourcesTable: React.FunctionComponent<NamespaceResourcesTableProps> = ({ namespace }) => {

  const github = new Github();

  const columnNames = {
    name: 'Name',
    ingress_urls: 'Ingress',
    status: 'Status',
    git_branch: 'Branch',
    git_commit: 'Commit',
    job_deploy: 'Deploy',
    job_date: 'Deployed'
  };

  const link = function (href, text) {
    return (<a href={href} target='_blank' rel='noopener noreferrer'>{text}</a>)
  }

  const strip_jenkins_job = function (job) {
    return job?.replace(/\/[^/]+\/$/, '/build');
  }

  const labels = {
    updating: { text: 'Updating', icon: <InProgressIcon />, color: 'blue' },
    creating: { text: 'Creating', icon: <InProgressIcon />, color: 'blue' },
    initializing: { text: 'Initializing', icon: <InProgressIcon />, color: 'blue' },
    patching: { text: 'Patching', icon: <WrenchIcon />, color: 'purple' },
    stopped: { text: 'Stopped', icon: <AsleepIcon />, color: 'grey' },
    running: { text: 'Running', icon: <RunningIcon />, color: 'green', severity: 'success' },
    warning: { text: 'Warning', icon: <ExclamationTriangleIcon />, color: 'orange', severity: 'warning' },
    outdated: { text: 'Outdated', icon: <ExclamationTriangleIcon />, color: 'gold', severity: 'warning' },
    stale: { text: 'Stale', icon: <ExclamationTriangleIcon />, color: 'gold', severity: 'warning' },
    failing: { text: 'Failing', icon: <ExclamationCircleIcon />, color: 'red', severity: 'danger' },
    unknown: { text: 'Unknown', icon: <OutlinedQuestionCircleIcon />, color: 'cyan' }
  }

  const reasons = {
    crashloopbackoff: {
      title: 'CrashLoopBackOff',
      message: (resource) => {
        let result = resource.description.match(/Exit code: ([0-9]+). Reason: (.*)/)
        let [exit_code, reason] = [result[1], result[2]]

        return <>
          The application is failing to start.
          <br /><br />
          The application finished with <b>code {exit_code}</b> because {reason}.
          <br />
          <br />
          Check the logs in kubernetes to determine the cause.
          <br />
          Check the <a href='https://teladochealth.atlassian.net/wiki/x/BN-qCw' target='_blank' rel='noopener noreferrer'>troubleshooting guides</a> for more information.
        </>
      }
    },
    createcontainerconfigerror: {
      title: 'CreateContainerConfigError',
      message: (resource) => {
        return <>
          The application deployment configuration is incorrect.
          <br /><br />
          Message: {resource.description}
          <br /><br />
          Check the {link('https://teladochealth.atlassian.net/wiki/x/BN-qCw', 'troubleshooting guides')} for more information.
        </>
      }
    },
    imagepullbackoff: {
      title: 'ImagePullBackOff',
      message: (resource) => {
        let image = resource.description.match(/Back-off pulling image "(.*)"/)[1]

        return <>
          The container image does not exist.
          <br /><br />
          Missing image: {image}
          <br /><br />
          Redeploy the application from {resource.deploy.job ? link(strip_jenkins_job(resource.deploy.job), 'jenkins') : 'jenkins'}.
        </>
      }
    },
    failing: {
      title: 'Failing',
      message: (resource) => {
        let events = Array.isArray(resource.description) ? resource.description : [resource.description];

        return <>
          The application is failing.
          <br /><br />
          Reasons:
          <List>
            {events.map(event => <ListItem>{event}</ListItem>)}
          </List>
          <br />
          Debug the application in kubernetes.
          Check the {link('https://teladochealth.atlassian.net/wiki/x/BN-qCw', 'troubleshooting guides')} for more information.
        </>
      }
    },
    disk_pressure: {
      title: 'DiskPressure',
      message: (resource) => {
        const diskList = Array.isArray(resource.description) ? resource.description : [resource.description];
        return <>
          The following disks are filling up. Increase the size or free space.
          <List>
            {diskList.map(val => {
              return <ListItem>{val}</ListItem>
            })}
          </List>
          <br />
          Check the {link('https://teladochealth.atlassian.net/wiki/x/YQ3rCw', 'mysql guide')} for more information.
          Access to kubernetes to perform any action.
        </>
      }
    },
    disk_full: {
      title: 'DiskFull',
      message: (resource) => {
        const diskList = Array.isArray(resource.description) ? resource.description : [resource.description];
        return <>
          The following disks are full. Increase the size or free space.
          <List>
            {diskList.map(val => {
              return <ListItem>{val}</ListItem>
            })}
          </List>
          <br />
          Check the {link('https://teladochealth.atlassian.net/wiki/x/YQ3rCw', 'mysql guide')} for more information.
          Access to kubernetes to perform any action.
        </>
      }
    },
    disk_unknown: {
      title: 'DiskUnknown',
      message: (resource) => {
        const diskList = Array.isArray(resource.description) ? resource.description : [resource.description];
        return <>
          Cannot determine the status of the following disks:
          <List>
            {diskList.map(val => {
              return <ListItem>{val}</ListItem>
            })}
          </List>
          <br />
          The most common reason is that the application got stuck in initializing state.
          <br /><br />
          Check the status of the application in kubernetes.
          Check the {link('https://teladochealth.atlassian.net/wiki/x/BN-qCw', 'troubleshooting guides')} for more information.
        </>
      }
    },
    running: {
      title: 'Running',
      message: (resource) => {
        return <>
          The application is up to date and seems to be healthy.
        </>
      }
    },
    patching: {
      title: 'Patching',
      message: (resource) => {
        return <>
          The application was failing and is being updated.
        </>
      }
    },
    unstable: {
      title: 'Unstable',
      message: (resource) => {
        return <>
          The application is running but has restarted in the lasts 10 mins.
          <br /><br />
          If this is alert doesn't go away check the last pod logs in kubernetes to determine the cause of restarts.
        </>
      }
    },
    stale: {
      title: 'Stale',
      message: (resource) => {

        let app_message: JSX.Element | undefined = undefined;
        let helm_message: JSX.Element | undefined = undefined;

        if (resource.deploy.diff.get(resource.git.branch) !== undefined) {
          const behind = resource.deploy.diff.get(resource.git.branch);
          const branch = resource.git.branch;

          if (behind > 0) {
            app_message = <>
              <List>
                <ListItem>
                  <i>{branch}</i> is <a href={github.compare(resource.deploy.repository, resource.deploy.commit, resource.git.branch)} target='_blank' rel='noopener noreferrer'>
                    {behind} changes ahead</a> of the deployed version. Create a <a href={github.compare(resource.helm.repository, resource.helm.branch, "master")} target='_blank' rel='noopener noreferrer'>
                    pull request</a> and <Link to="/granular-deploy">redeploy</Link>.
                </ListItem>
              </List></>
          } else {
            app_message = <List><ListItem>The application is up to date.</ListItem></List>
          }
        } else {
          app_message = <List><ListItem>The application is up to date.</ListItem></List>
        }


        if (resource.helm.diff.get("master") !== undefined) {
          const behind = resource.helm.diff.get("master");
          const branch = resource.helm.branch;

          if (behind > 0) {
            helm_message = <>
              <List>
                <ListItem>
                  <i>master</i> is <a href={github.compare(resource.helm.repository, resource.helm.commit, "master")} target='_blank' rel='noopener noreferrer'>
                    {behind} changes ahead</a> of the deployed version. Create a <a href={github.compare(resource.helm.repository, resource.helm.branch, "master")} target='_blank' rel='noopener noreferrer'>
                    pull request</a> to update branch {resource.helm.branch} and <Link to="/granular-deploy">redeploy</Link>.
                </ListItem>
              </List></>
          } else {
            helm_message = <List><ListItem>The deploy config is up to date.</ListItem></List>
          }
        } else {
          helm_message = <List><ListItem>Unable to get deploy config sync status due to the deployment not being linked to the deploy repo. </ListItem></List>
        }

        return (<>
          The version is up to date with the selected feature branch but is missing changes from the repository main branch.
          <br /><br />
          <b>Application repository</b><br />
          {app_message}
          <br />
          <b>Deploy config (helm)</b><br />
          {helm_message}
          <br />
          Go to <Link to="/granular-deploy">Manage Environment</Link> to deploy the latest changes. {link('https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/199944265/EOD+Dashboard+Outdated+application#id-[EODDashboard]Outdatedapplication-stale', 'Click here')} for more information.
        </>
        )

      }
    },
    outdated: {
      title: 'Outdated',
      message: (resource) => {
        if (resource.description.includes('The repository is not registered')) {
          return (<>
            It is not possible to determine if the deployment is up to date.
            <br /><br />
            Reason: The application is not registered in the company's application database.
            There is no information about the application repository or main branch.
            <br /><br />
            Access the {link('https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/199944265/EOD+Dashboard+Outdated+application#id-[EODDashboard]Outdatedapplication-notregistered', 'guide')} for more information.
          </>)
        }

        if (resource.description.includes("Couldn't get the deployed branch")) {
          return (<>
            It is not possible to determine if the deployment is up to date.
            <br /><br />
            Reason: The deployment is not annotated with the branch. This could be because it was deployed
            prior to the implementation of the dashboard and the deployment is missing the metadata.
            <br /><br />
            This should be fixed redeploying the application. {resource.deploy.job ? <a href={strip_jenkins_job(resource.deploy.job)} target='_blank' rel='noopener noreferrer'>Link to jenkins</a> : undefined}
            <br /><br />
            Access the {link('https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/199944265/EOD+Dashboard+Outdated+application#id-[EODDashboard]Outdatedapplication-notpossible', 'guide')} for more information.
          </>)
        }

        if (resource.description.includes("Couldn't get the deployed commit")) {
          return (<>
            It is not possible to determine if the deployment is up to date.
            <br /><br />
            Reason: The deployment is not annotated with the commit. This could be because it was deployed
            prior to the implementation of the dashboard and the deployment is missing the metadata.
            <br /><br />
            This should be fixed redeploying the application. {resource.deploy.job ? <a href={strip_jenkins_job(resource.deploy.job)} target='_blank' rel='noopener noreferrer'>Link to jenkins</a> : undefined}
            <br /><br />
            Access the {link('https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/199944265/EOD+Dashboard+Outdated+application#id-[EODDashboard]Outdatedapplication-notpossible', 'guide')} for more information.
          </>)
        }

        let app_messages = Array.from(resource.deploy.diff.entries() as Iterable<[string, number | undefined]>).reverse().map(
          ([branch, behind]) => {
            if (behind !== undefined && behind > 0) {
              if (resource.deploy.branch == branch) {
                return (<>
                  <i>{branch}</i> is <a href={github.compare(resource.deploy.repository, resource.deploy.commit, resource.deploy.branch)} target='_blank' rel='noopener noreferrer'>
                    {behind} changes ahead</a> of the deployed version. <Link to="/granular-deploy">Redeploy</Link> to update the app.
                </>)
              } else {
                return (<>
                  <i>{branch}</i> is <a href={github.compare(resource.deploy.repository, resource.deploy.commit, resource.git.branch)} target='_blank' rel='noopener noreferrer'>
                    {behind} changes ahead </a> of the deployed version. Create a <a href={github.compare(resource.deploy.repository, resource.deploy.branch, resource.git.branch)} target='_blank' rel='noopener noreferrer'>
                    pull request</a> and <Link to="/granular-deploy">redeploy</Link>.
                </>)
              }

            } else if (behind == undefined) {
              return (<>
                <i>{branch}</i> was not found, possibly because it was merged and deleted.
              </>)
            }
            return undefined
          }).filter(el => el !== undefined)


        let helm_messages: JSX.Element[] | undefined
        if (Array.from(resource.helm.diff.entries()).length > 0) {
          helm_messages = Array.from(resource.helm.diff.entries() as Iterable<[string, number | undefined]>).reverse().map(

            ([branch, behind]) => {
              if (behind !== undefined && behind > 0) {
                if (resource.helm.branch == branch) {
                  return (<>
                    <i>{branch}</i> is <a href={github.compare(resource.helm.repository, resource.helm.commit, resource.helm.branch)} target='_blank' rel='noopener noreferrer'>
                      {behind} changes ahead</a> of the deployed version. <Link to="/granular-deploy">Redeploy</Link> to update the app.
                  </>)
                } else {
                  return (<>
                    <i>{branch}</i> is <a href={github.compare(resource.helm.repository, resource.helm.commit, "master")} target='_blank' rel='noopener noreferrer'>
                      {behind} changes ahead</a> of the deployed version. Create a <a href={github.compare(resource.helm.repository, resource.helm.branch, "master")} target='_blank' rel='noopener noreferrer'>
                      pull request</a> to update branch {resource.helm.branch} and <Link to="/granular-deploy">redeploy</Link>.
                  </>)
                }

              } else if (behind == undefined) {
                return (<>
                  <i>{branch}</i> was not found, possibly because it was merged and deleted.
                </>)
              }
              return undefined
            }).filter((el): el is JSX.Element => el !== undefined)
        }

        return (<>
          The deployed version is missing changes from the git repository.
          <br /><br />

          <b>Application repository</b><br />
          <>
            {app_messages.length > 0 ?
              <List>
                {app_messages.map(component => {
                  return (<ListItem>{component}</ListItem>)
                })}
              </List> : <List><ListItem>The application is up to date.</ListItem></List>
            }
            <br />
          </>

          <b>Deploy config (helm)</b><br />
          <>
            {helm_messages === undefined ?
              <List><ListItem>Unable to get deploy config sync status due to the deployment not being linked to the deploy repo.</ListItem></List> : 
              (helm_messages.length > 0 ?
                <List>
                  {helm_messages.map(component => {
                    return (<ListItem>{component}</ListItem>)
                  })}
                </List> : <List><ListItem>The deploy config is up to date.</ListItem></List>)
            }
            <br />
          </>
      
          Go to <Link to="/granular-deploy">Manage Environment</Link> to deploy the latest changes. {link('https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/199944265/EOD+Dashboard+Outdated+application#id-[EODDashboard]Outdatedapplication-outdated', 'Click here')} for more information.
        </>)
      }
    },
    stopped: {
      title: 'Stopped',
      message: (resource) => {
        return (<>
          The application is stopped. <br /><br />
          Use the<a href={globalThis.cluster.JENKINS_DEPLOY_URL + '/job/env.wakeup/build'} target='_blank' rel='noopener noreferrer'>wake.up job</a> or deploy it again from {resource.deploy.job ? link(strip_jenkins_job(resource.deploy.job), 'jenkins') : 'jenkins'}.
        </>)
      }
    }
  }


  const renderStatusLabel = (resource) => {
    const values = labels[resource.status];
    const reason = resource.reason ? resource.reason : resource.status;

    if (reasons[reason] == null) {
      return <span className={`dx-label dx-label--${values.color}`}><span className="dx-label__icon">{values.icon}</span>{values.text}</span>
    } else {
      return (
        <Popover
          triggerAction="hover"
          position='right-end'
          alertSeverityVariant={values.severity}
          headerIcon={values.icon}
          aria-label="Hoverable popover"
          headerContent={reasons[reason].title}
          bodyContent={
            <div>
              {reasons[reason].message(resource)}
            </div>
          }
          footerContent={undefined}
        >
          <span className={`dx-label dx-label--${values.color}`}><span className="dx-label__icon">{values.icon}</span>{values.text}</span>
        </Popover>
      )
    }
  }

  const [activeSortIndex, setActiveSortIndex] = React.useState<number>(0);
  const [activeSortDirection, setActiveSortDirection] = React.useState<'asc' | 'desc' | undefined>('asc');

  const getSortParams = (columnIndex: number): ThProps['sort'] => ({
    sortBy: {
      index: activeSortIndex,
      direction: activeSortDirection,
      defaultDirection: 'asc' // starting sort direction when first sorting a column. Defaults to 'asc'
    },
    onSort: (_event, index, direction) => {
      setActiveSortIndex(index);
      setActiveSortDirection(direction);
    },
    columnIndex
  });

  const getSortableRowValues = (resource: Resource): (string | number | Date | undefined)[] => {
    const { name, status, deploy } = resource;
    return [name, status, deploy.branch, deploy.date?.getTime()];
  };

  const getResourceIngressHosts = (resource: Resource): { hasIngresses: boolean; hosts: string[] } => {
    const allIngresses = [
      ...(namespace.ingresses || []),
      ...(namespace.ingressinternals || []),
    ] as Array<{ name?: string; labels?: Record<string, string>; hosts?: string[] }>;

    const namespacePrefix = `${namespace.name}-${resource.name}-`;
    const hosts = new Set<string>();
    let hasIngresses = false;

    allIngresses.forEach(ing => {
      const ingressName = ing?.name || '';
      if (ingressName.startsWith(namespacePrefix)) {
        hasIngresses = true;
        (ing.hosts || []).forEach(h => hosts.add(h));
      }
    });

    return { hasIngresses, hosts: Array.from(hosts) };
  };

  if (activeSortDirection !== undefined) {
    namespace.resources.sort((a, b) => {
      const aValue = getSortableRowValues(a)[activeSortIndex];
      const bValue = getSortableRowValues(b)[activeSortIndex];
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        if (activeSortDirection === 'asc') {
          return (bValue as number) - (aValue as number);
        }
        return (aValue as number) - (bValue as number);

      } else {
        if (activeSortDirection === 'asc') {
          return (aValue as string).localeCompare(bValue as string);
        }
        return (bValue as string).localeCompare(aValue as string);
      }

    })
  }

  return (
    <Table variant={'compact'}>
      <Thead>
        <Tr>
          <Th sort={getSortParams(0)}>{columnNames.name}</Th>
          <Th style={{ textTransform: 'none' }}>{columnNames.ingress_urls}</Th>
          <Th sort={getSortParams(1)} >{columnNames.status}</Th>
          <Th sort={getSortParams(2)} >{columnNames.git_branch}</Th>
          <Th >{columnNames.git_commit}</Th>
          <Th>{columnNames.job_deploy}</Th>
          <Th sort={getSortParams(3)}>{columnNames.job_date}</Th>
        </Tr>
      </Thead>
      <Tbody>
        {
          namespace.resources?.map((resource) => (
            <Tr key={resource.name + "-" + resource.type}>
              <Td dataLabel={columnNames.name}>{resource.name}</Td>
              <Td dataLabel={columnNames.ingress_urls}>
                {(() => {
                  const { hasIngresses, hosts } = getResourceIngressHosts(resource);
                  if (!hasIngresses) return null;
                  if (hosts.length === 0) return <span>—</span>;
                  return (
                    <Popover
                      triggerAction="hover"
                      position="right-end"
                      headerContent="Ingress URLs"
                      bodyContent={
                        <List isPlain>
                          {hosts.map(host => (
                            <ListItem key={host}>
                              <a href={`https://${host}`} target="_blank" rel="noopener noreferrer">{host}</a>
                            </ListItem>
                          ))}
                        </List>
                      }
                    >
                      <span style={{ cursor: 'default', color: '#0066cc', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <NetworkIcon />{hosts.length}
                      </span>
                    </Popover>
                  );
                })()}
              </Td>

              <Td dataLabel={columnNames.status}>
                {renderStatusLabel(resource)}
              </Td>
              <Td dataLabel={columnNames.git_branch}>
                {
                  resource.deploy.repository && resource.deploy.branch ?
                    <a target='_blank' rel='noopener noreferrer' href={github.tree(resource.deploy.repository, resource.deploy.branch)}>
                      {resource.deploy.branch}
                    </a>
                    :
                    resource.deploy.branch
                }
              </Td>
              <Td dataLabel={columnNames.git_commit}>
                {
                  resource.deploy.repository && resource.deploy.commit ?
                    <a target='_blank' rel='noopener noreferrer' href={github.commit(resource.deploy.repository, resource.deploy.commit)}>
                      {resource.deploy.commit?.slice(0, 7)}
                    </a>
                    :
                    resource.deploy.commit?.slice(0, 7)
                }
              </Td>
              <Td dataLabel={columnNames.job_deploy}>
                {resource.deploy.job ? <a href={resource.deploy.job} target="_blank" rel="noopener noreferrer">{
                  resource.deploy.job?.match(/\/(\d+)\/$/)?.[1] ? '#' + resource.deploy.job?.match(/\/(\d+)\/$/)?.[1] : 'Link'

                }</a> : ''}
              </Td>
              <Td dataLabel={columnNames.job_date}>
                {resource.deploy.date != undefined ? <Timestamp date={resource.deploy.date} relative /> : ''}
              </Td>
            </Tr>
          ))
        }
      </Tbody>
    </Table>
  )
}

export { NamespaceResourcesTable }
